// BE#647 defect-2 — "what did this step ACTUALLY send?".
//
// A drip step is a campaign that mints one recipient message per contact from the
// LIVE template. Once a step has sent, its card used to render the step's *current*
// `template` — which an in-flight edit overwrites — so an already-sent step showed
// the NEW (edited) body labelled "Sent N/N", a lie. With the re-mint fix (PR #649)
// a step's recipients can legitimately hold DIFFERENT bodies (some minted before
// the edit, some re-minted after). The truth of what went out lives only in each
// recipient's sent message-log snapshot (`log.message`, the embedded userMessage).
//
// This module derives, from a campaign's loaded delivery logs, whether the step's
// recipients all got the SAME content (`uniform` → show that body), got DIFFERENT
// content (`mixed` → point at the per-recipient delivery log), or nothing is loaded
// yet (`none`). Pure + framework-free so it unit-tests without the component chain.
import type { MessageLog } from '@common/models/message-log';
import type { UserMessage } from '@bt-common/schemas/bt/user-message';
import { collapseDoubleBraces } from './primitives';

export type SentContentState =
    | { kind: 'none' }
    | { kind: 'uniform'; message: UserMessage }
    | { kind: 'mixed' };

// What the step card's preview should render. Kept pure (no React) so the
// "a sent step shows what was SENT, never its edited template" decision is
// unit-tested in the node test env without a component-render harness.
export type StepPreviewMode =
    | { kind: 'sent-uniform'; message: UserMessage } // one body reached everyone → show it
    | { kind: 'sent-mixed' } // recipients differ → point at the delivery log
    | { kind: 'sent-loading' } // sent, but logs not loaded yet → neutral, NEVER the template
    | { kind: 'template' }; // not-yet-sent (or no sent data) → the (pending) template

/** Decide what a step card previews. A sent/in-flight step previews what actually
 *  went out; only a not-yet-sent step previews its own template. */
export function stepPreviewMode(isSentStep: boolean, sentContent?: SentContentState): StepPreviewMode {
    if (isSentStep && sentContent) {
        if (sentContent.kind === 'uniform') return { kind: 'sent-uniform', message: sentContent.message };
        if (sentContent.kind === 'mixed') return { kind: 'sent-mixed' };
        return { kind: 'sent-loading' }; // 'none'
    }
    return { kind: 'template' };
}

// A single-brace merge token `{First Name}` (the send-time resolver also accepts
// the Handlebars-habit `{{token}}`, which `collapseDoubleBraces` normalises first).
const MERGE_TOKEN = /\{[^{}\n]+\}/;

/** Does the step's template personalise per recipient? When it does, the sent
 *  bodies differ by design (substituted names), so the text must be excluded from
 *  the uniform-vs-mixed comparison — otherwise every personalised send reads as a
 *  spurious "mix". */
export function templateUsesMergeFields(message?: string | null, pollQuestion?: string | null): boolean {
    const has = (s?: string | null) => (s ? MERGE_TOKEN.test(collapseDoubleBraces(s)) : false);
    return has(message) || has(pollQuestion);
}

function assetSignature(asset: UserMessage['asset']): string | null {
    if (!asset) return null;
    // Merge-invariant: an image is the same file for every recipient, so these
    // fields tell an image-removal / image-swap edit apart from personalization.
    return JSON.stringify({
        url: asset.url ?? null,
        type: asset.type ?? null,
        name: asset.name ?? null,
        isGif: asset.isGif ?? false,
    });
}

function pollSignature(m: UserMessage, includeText: boolean): unknown {
    if (!m.isPoll || !m.poll) return null;
    const options = m.poll.options ?? [];
    return {
        count: options.length, // merge-invariant: an edit that adds/removes an option is a real change
        question: includeText ? (m.poll.question ?? '').trim() : undefined,
        options: includeText ? options.map((o) => (o?.text ?? '').trim()) : undefined,
    };
}

/** A content fingerprint. With `includeText` (no merge fields) the substituted
 *  body text is part of it, so an edited text body reads as a mix. Without it
 *  (merge fields present) only merge-invariant structure counts, so a name swap
 *  is not a mix but an image/poll-structure change still is. */
function contentSignature(m: UserMessage, includeText: boolean): string {
    return JSON.stringify({
        text: includeText ? (m.message ?? '').trim() : undefined,
        asset: assetSignature(m.asset),
        isPoll: !!m.isPoll,
        poll: pollSignature(m, includeText),
    });
}

/**
 * From a campaign's loaded delivery logs, what did the step actually send?
 *   - `none`    — no loaded log carries a sent snapshot for this campaign yet.
 *   - `uniform` — every recipient got the same content; `.message` is a representative snapshot.
 *   - `mixed`   — recipients got different content (an in-flight edit landed mid-send).
 * `usesMergeFields` excludes the personalised text from the comparison (see above).
 */
export function deriveSentContent(
    logs: Record<string, MessageLog | undefined> | MessageLog[] | undefined,
    campaignId: string | undefined,
    opts?: { usesMergeFields?: boolean },
): SentContentState {
    if (!campaignId || !logs) return { kind: 'none' };
    const all = Array.isArray(logs) ? logs : Object.values(logs);
    const relevant = all.filter((l): l is MessageLog => !!l && l.campaignId === campaignId && !!l.message);
    if (relevant.length === 0) return { kind: 'none' };

    const includeText = !opts?.usesMergeFields;
    const signatures = new Set(relevant.map((l) => contentSignature(l.message, includeText)));
    return signatures.size === 1 ? { kind: 'uniform', message: relevant[0].message } : { kind: 'mixed' };
}
